/**
 * Encrypted web storage adapter — encryption at rest for the Supabase auth
 * session on web (compliance gap G13).
 *
 * On web, supabase-js defaults to `localStorage`, which stores the access +
 * refresh token in plaintext on the user's disk (readable by any same-origin
 * script and by anything that dumps localStorage). Mobile already encrypts the
 * session via `expo-secure-store` (Keychain/Keystore); this brings web to
 * parity by encrypting values at rest with AES-GCM before they touch
 * localStorage.
 *
 * Key management: a 256-bit AES-GCM key is generated ONCE and stored in
 * IndexedDB as a **non-extractable** `CryptoKey` — it can be used to
 * encrypt/decrypt but its raw bytes can never be read back out by script, so
 * the key itself is not exfiltratable the way a plaintext token is.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHAT THIS DOES **NOT** PROTECT AGAINST — read this before citing it.
 *
 * This is encryption AT REST, and nothing more. It does NOT defend against an
 * active same-origin XSS, and it must never be described as if it does.
 *
 * The reason is structural, not a gap in the implementation: injected script
 * runs in this origin, with this page's access to IndexedDB. It does not need
 * to read the key — it simply calls `getItem()`, or `supabase.auth.getSession()`,
 * and receives the decrypted token, because decrypting for the app is exactly
 * this module's job. A non-extractable CryptoKey stops the KEY being stolen; it
 * cannot stop the PLAINTEXT being requested. The realistic threats it does
 * close are offline ones: another process reading the profile directory, a disk
 * image, a synced browser profile, a `localStorage` dump from a shared machine.
 *
 * The primary controls against active XSS are, in order:
 *   1. not injecting script in the first place — no user-controlled content
 *      reaching `dangerouslySetInnerHTML`, dynamic style/DOM sinks, or
 *      `eval`-shaped APIs (guarded by src/lib/__tests__/security-headers.test.ts);
 *   2. Content-Security-Policy (vercel.json — currently Report-Only; see
 *      docs/security/auth-deployment-checklist.md for the path to enforcement).
 *
 * An httpOnly-cookie session model would additionally put the token out of
 * script's reach entirely, which is the only thing that would change the
 * paragraph above — tracked separately as SEC-FE-01.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Safety-first design — this sits on the auth path, so it must NEVER lock a
 * user out:
 *   - Backward compatible: `getItem` transparently reads pre-existing plaintext
 *     values (migrated to ciphertext on the next `setItem`).
 *   - Graceful fallback: if the platform lacks a secure context, SubtleCrypto,
 *     or IndexedDB, it falls back to plain `localStorage` — never worse than the
 *     previous behaviour, never blocking sign-in.
 *   - Fail-open on read errors returns `null` (treated as "no session" → a
 *     re-login), never a thrown error that would wedge the auth listener.
 */

const ENC_PREFIX = 'enc:v1:';
const IDB_NAME = 'inverio-secure';
const IDB_STORE = 'keys';
const IDB_KEY_ID = 'auth-key';

type WebStorage = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

/** True only in a browser with the crypto + storage primitives we need. */
function isSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined' &&
    typeof indexedDB !== 'undefined' &&
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined' &&
    // SubtleCrypto is only available in a secure context (https / localhost).
    (window.isSecureContext ?? false)
  );
}

// ---- base64 <-> bytes (binary-safe) -----------------------------------------
function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---- IndexedDB-backed non-extractable key -----------------------------------
function idbGetOrPut(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(IDB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(IDB_STORE);
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const db = open.result;
      const tx = db.transaction(IDB_STORE, mode);
      const req = fn(tx.objectStore(IDB_STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    };
  });
}

let keyPromise: Promise<CryptoKey> | null = null;
function getKey(): Promise<CryptoKey> {
  if (!keyPromise) {
    keyPromise = (async () => {
      const existing = (await idbGetOrPut('readonly', (s) => s.get(IDB_KEY_ID))) as CryptoKey | undefined;
      if (existing) return existing;
      const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        false, // non-extractable — raw bytes can never be read back out
        ['encrypt', 'decrypt'],
      );
      await idbGetOrPut('readwrite', (s) => s.put(key, IDB_KEY_ID));
      return key;
    })().catch((err) => {
      // Reset so a transient failure can retry on the next call.
      keyPromise = null;
      throw err;
    });
  }
  return keyPromise;
}

async function encrypt(plaintext: string): Promise<string> {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  return `${ENC_PREFIX}${bytesToB64(iv)}:${bytesToB64(new Uint8Array(ct))}`;
}

async function decrypt(value: string): Promise<string> {
  const [ivB64, ctB64] = value.slice(ENC_PREFIX.length).split(':');
  const key = await getKey();
  const iv = b64ToBytes(ivB64) as BufferSource;
  const ct = b64ToBytes(ctB64) as BufferSource;
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return new TextDecoder().decode(pt);
}

/**
 * Build the Supabase `auth.storage` adapter for web. Returns `undefined` when
 * the platform can't support encryption, so the caller keeps supabase-js's
 * default (plain localStorage) rather than breaking auth.
 */
export function createWebSecureStorage(): WebStorage | undefined {
  if (!isSupported()) return undefined;

  return {
    async getItem(key) {
      try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return null;
        if (!raw.startsWith(ENC_PREFIX)) return raw; // legacy plaintext — migrated on next setItem
        return await decrypt(raw);
      } catch {
        // Key loss / corrupt value → behave as "no session" (re-login), never throw.
        return null;
      }
    },
    async setItem(key, value) {
      try {
        window.localStorage.setItem(key, await encrypt(value));
      } catch {
        // Never block sign-in: fall back to plaintext if encryption is unavailable.
        try { window.localStorage.setItem(key, value); } catch { /* storage full / blocked */ }
      }
    },
    async removeItem(key) {
      try { window.localStorage.removeItem(key); } catch { /* ignore */ }
    },
  };
}
